import React from 'react'
import { useSequencerStore, PADS_PER_PROJECT } from '@/stores/sequencerStore'
import { TrackerInspector } from './tracker/TrackerInspector'
import { PadSettingsForm } from './PadSettingsForm'

type EditorTab = 'cell' | 'pad'

/**
 * Always-visible per-step / per-pad inspector docked at the bottom
 * of the pad column. Replaces the previous `StepInspectorOverlay`
 * popover so the user can see the focused cell's / pad's editable
 * state at a glance without having to long-press or open a drawer.
 *
 * Two tabs:
 *  - **Cell**  — per-step controls (velocity, pitch, FX, envelope,
 *                roll, …). Visible when a cell is focused.
 *  - **Pad**   — per-pad settings that apply across every step of
 *                the focused pad. Visible whenever a pad index is
 *                in range, regardless of cell focus. Body arrives
 *                in the C2 PR; this PR ships the scaffolding + tab
 *                routing only.
 *
 * The tab strip auto-selects the right tab when focus changes: a
 * cell focus defaults to the Cell tab; pad-only focus defaults to
 * Pad. The user can flip manually via the strip.
 */
export const StepInspectorPanel: React.FC = () => {
  const cellFocus = useSequencerStore(s => s.trackerFocus)
  const focusedPad = useSequencerStore(s => s.focusedPad)
  const padInRange = focusedPad >= 0 && focusedPad < PADS_PER_PROJECT
  const cellTabEnabled = cellFocus !== null
  const padTabEnabled = padInRange

  // Auto-select tab on focus change. If the only available tab
  // flips, jump to it — otherwise preserve whatever the user last
  // chose. Kept in a ref-backed state so we don't thrash when
  // both tabs become available simultaneously.
  const [activeTab, setActiveTab] = React.useState<EditorTab>('cell')
  React.useEffect(() => {
    if (cellTabEnabled && !padTabEnabled) setActiveTab('cell')
    else if (!cellTabEnabled && padTabEnabled) setActiveTab('pad')
    // Both available → leave the user's current choice alone.
  }, [cellTabEnabled, padTabEnabled])

  const focusLabel = cellFocus
    ? `${cellFocus.row.kind === 'kit' ? `Pad ${cellFocus.row.padIndex + 1}` : 'MIDI pad'} · step ${cellFocus.stepIndex + 1}`
    : padInRange
      ? `Pad ${focusedPad + 1}`
      : ''

  return (
    <div className="rounded-xl bg-surface-sunken border border-brd-subtle flex flex-col min-h-0 flex-1 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-brd-subtle shrink-0 gap-3">
        <div className="flex items-center gap-1">
          <TabButton
            label="Cell"
            active={activeTab === 'cell'}
            disabled={!cellTabEnabled}
            onClick={() => setActiveTab('cell')}
            title={
              cellTabEnabled
                ? 'Per-step controls: velocity, pitch, FX sends, envelope, roll'
                : 'Click a step cell in the grid to enable this tab'
            }
          />
          <TabButton
            label="Pad"
            active={activeTab === 'pad'}
            disabled={!padTabEnabled}
            onClick={() => setActiveTab('pad')}
            title={
              padTabEnabled
                ? 'Per-pad settings: kind, routing, bus, sample, envelope'
                : 'Focus a pad to enable this tab'
            }
          />
        </div>
        {focusLabel && (
          <span className="text-[10px] font-mono text-content-tertiary truncate">
            {focusLabel}
          </span>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-3">
        {activeTab === 'cell' &&
          (cellFocus ? (
            <TrackerInspector />
          ) : (
            <p className="text-[11px] text-content-tertiary leading-relaxed">
              Click (or long-press) any step cell in the grid to edit its
              velocity, pitch, length, FX sends, envelope, and roll.
            </p>
          ))}
        {activeTab === 'pad' &&
          (padInRange ? (
            <PadSettingsForm padIndex={focusedPad} />
          ) : (
            <p className="text-[11px] text-content-tertiary leading-relaxed">
              Focus a pad to edit its settings.
            </p>
          ))}
      </div>
    </div>
  )
}

const TabButton: React.FC<{
  label: string
  active: boolean
  disabled: boolean
  onClick: () => void
  title: string
}> = ({ label, active, disabled, onClick, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-pressed={active}
    title={title}
    className={`px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-[0.12em] border transition-colors ${
      active
        ? 'bg-accent text-[color:var(--accent-ink)] border-accent'
        : disabled
          ? 'border-brd-subtle text-content-tertiary opacity-50 cursor-not-allowed'
          : 'border-brd-subtle text-content-secondary hover:border-accent hover:text-accent'
    }`}
  >
    {label}
  </button>
)
